import React, { useId } from "react";
import Badge from "components/badge";
import { InputField, Textarea } from "components/fields";
import ContinuousSlider from "components/heartbeat-survey/ContinuousSlider";
import ChoiceOption from "views/public/shared/ChoiceOption";
import { CATEGORY_LABELS } from "../spiritualContent";

/**
 * One heartbeat survey question. Types: slider/scale (1–5 continuous), text,
 * textarea, single_choice and multiple_choice.
 */
const SurveyQuestion = ({ question, answer, onAnswer, showCategory }) => {
  const headingId = useId();
  const { type } = question;

  return (
    <>
      {showCategory && question.category && (
        <Badge tone="neutral" className="mb-3">
          {CATEGORY_LABELS[question.category] || "General"}
        </Badge>
      )}

      <h1 id={headingId} className="mb-6 text-title leading-snug sm:text-3xl">
        {question.text}
      </h1>

      {(type === "slider" || type === "scale") && (
        <ContinuousSlider
          value={answer || 3}
          onChange={onAnswer}
          lowLabel={question.lowLabel || "Rarely"}
          highLabel={question.highLabel || "Always"}
          aria-labelledby={headingId}
        />
      )}

      {type === "text" && (
        <InputField
          aria-labelledby={headingId}
          value={answer || ""}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder="Type your answer…"
        />
      )}

      {type === "textarea" && (
        <Textarea
          aria-labelledby={headingId}
          value={answer || ""}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder="Type your answer…"
          rows={4}
        />
      )}

      {type === "single_choice" && (
        <div role="group" aria-labelledby={headingId} className="flex flex-col gap-3">
          {(question.options || []).map((option, idx) => (
            <ChoiceOption
              key={idx}
              selected={answer === option}
              onClick={() => onAnswer(option)}
            >
              {option}
            </ChoiceOption>
          ))}
        </div>
      )}

      {type === "multiple_choice" && (
        <div role="group" aria-labelledby={headingId} className="flex flex-col gap-3">
          {(question.options || []).map((option, idx) => {
            const selectedOptions = answer || [];
            const isSelected = selectedOptions.includes(option);
            return (
              <ChoiceOption
                key={idx}
                multiple
                selected={isSelected}
                onClick={() =>
                  onAnswer(
                    isSelected
                      ? selectedOptions.filter((o) => o !== option)
                      : [...selectedOptions, option]
                  )
                }
              >
                {option}
              </ChoiceOption>
            );
          })}
        </div>
      )}
    </>
  );
};

export default SurveyQuestion;
